import { useState, useEffect, useRef } from "react";

function Layout({
  file,
  selectedRoom,
  data,
  nestedClasses,
  selectedItem,
  setData,
}) {
  const floorPlanRef = useRef(null);
  const containerRef = useRef(null);
  const [elements, setElements] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isWallCreationMode, setIsWallCreationMode] = useState(false);
  const [isSpaceCreationMode, setIsSpaceCreationMode] = useState(false);
  const [tempData, setTempData] = useState([]);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [startDrag, setStartDrag] = useState({ x: 0, y: 0 });
  const [translate, setTranslate] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (data) {
      drawLayout(data);
    }
  }, [data]);

  function drawLayout(data) {
    const newElements = data.map((item) => ({
      ...item,
      x: item.x - item.width / 2,
      y: item.y - item.height / 2,
      type: item.class,
    }));
    setElements(newElements);
  }

  const startDrawing = (e) => {
    if (isWallCreationMode === isSpaceCreationMode) return;
    setIsDrawing(true);
    const rect = e.target.getBoundingClientRect();
    const x = (e.clientX - rect.left) / zoomLevel;
    const y = (e.clientY - rect.top) / zoomLevel;
    const elementType = isWallCreationMode ? "Walls" : "space";
    const newElement = { x, y, width: 0, height: 0, type: elementType };
    setElements((prev) => [...prev, newElement]);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const rect = floorPlanRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / zoomLevel;
    const y = (e.clientY - rect.top) / zoomLevel;
    const elementsCopy = [...elements];
    const currentElement = elementsCopy[elementsCopy.length - 1];
    currentElement.width = x - currentElement.x;
    currentElement.height = y - currentElement.y;
    setElements(elementsCopy);

    const newData = [
      ...data,
      {
        x: currentElement.x + currentElement.width / 2,
        y: currentElement.y + currentElement.height / 2,
        width: currentElement.width,
        height: currentElement.height,
        class: currentElement.type,
      },
    ];
    setTempData(newData);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    if (tempData && tempData.length > 0) {
      setData(tempData);
      setTempData([]);
    }
  };

  const toggleWallCreationMode = () => {
    if (!selectedItem && !selectedRoom) {
      setIsWallCreationMode((prev) => !prev);
      setIsSpaceCreationMode(false);
    }
  };

  const toggleSpaceCreationMode = () => {
    if (!selectedItem && !selectedRoom) {
      setIsSpaceCreationMode((prev) => !prev);
      setIsWallCreationMode(false);
    }
  };

  const zoomIn = () => {
    setZoomLevel((prevZoomLevel) => Math.min(prevZoomLevel * 1.1, 2));
  };

  const zoomOut = () => {
    setZoomLevel((prevZoomLevel) => {
      const newZoomLevel = Math.max(prevZoomLevel / 1.1, 1);
      if (newZoomLevel === 1) {
        setTranslate({ x: 0, y: 0 });
      }
      return newZoomLevel;
    });
  };
  const getElementStyle = (element) => {
    const allowedRoomTypes = [
      "Living Room",
      "room",
      "Bedroom",
      "kitchen",
      "garage",
      "Bathroom",
      "Balcony",
      "staircase",
      "Wardrobe",
      "DiningRoom",
      "Staircase",
      "Master Bedroom",
      "office",
      "StoreRoom",
      "Utility",
      "PrayerRoom",
      "BalconyRailing",
      "StudyRoom",
      "Office",
      "window",
      "door",
      "Walls",
      "space",
    ];

    const isRoom = allowedRoomTypes.includes(element.type);
    var colorMap;

    if (isRoom) {
      colorMap = {
        room:
          selectedRoom || selectedItem
            ? ""
            : `rgba(${element.x},${element.width},${element.y},0.3)`,
        "Living Room":
          selectedRoom || selectedItem
            ? ""
            : `rgba(${element.x},${element.width},${element.y},0.3)`,
        Bedroom:
          selectedRoom || selectedItem
            ? ""
            : `rgba(${element.x},${element.width},${element.y},0.3)`,
        window: "lightblue",
        Bathroom:
          selectedRoom || selectedItem
            ? ""
            : `rgba(${element.x},${element.width},${element.y},0.3)`,
        kitchen:
          selectedRoom || selectedItem
            ? ""
            : `rgba(${element.x},${element.width},${element.y},0.3)`,
        Garage:
          selectedRoom || selectedItem
            ? ""
            : `rgba(${element.x},${element.width},${element.y},0.3)`,
        lounge:
          selectedRoom || selectedItem
            ? ""
            : `rgba(${element.x},${element.width},${element.y},0.3)`,
        wall: "rgba(0, 0, 0, 0.2)",
        [selectedRoom.class]:
          selectedRoom.x == element.x && selectedRoom.y == element.y
            ? `rgba(${element.x},${element.width},${element.y}, 0.3)`
            : "",
        Space: "rgba(0, 0, 0, 0.2)",
        [selectedRoom.class]:
          selectedRoom.x == element.x && selectedRoom.y == element.y
            ? `rgba(${element.x},${element.width},${element.y}, 0.3)`
            : "",
      };
    } else if (selectedItem) {
      if (
        selectedItem.x == element.x &&
        selectedItem.y == element.y &&
        selectedItem.width == element.width &&
        selectedItem.height == element.height
      ) {
        colorMap = "";
      } else {
        const associatedRoom = nestedClasses.find((room) =>
          room.contains.some(
            (item) => item.class == element.type && item.x == element.x
          )
        );
        colorMap =
          selectedItem &&
          selectedItem.class == element.type &&
          selectedItem.width == element.width &&
          selectedItem.height == element.height
            ? associatedRoom
              ? "rgba(0, 128, 0, 0.5)" 
              : "rgba(255, 0, 0, 0.5)" 
            : "";
      }
    } else {
      const associatedRoom = nestedClasses.find((room) =>
        room.contains.some(
          (item) => item.class == element.type && item.x == element.x
        )
      );
      colorMap = associatedRoom
        ? "rgba(0, 128, 0, 0.5)"
        : "rgba(255, 0, 0, 0.5)"; 
    }

    return {
      position: "absolute",
      left: `${element.x}px`,
      top: `${element.y}px`,
      width: `${element.width}px`,
      height: `${element.height}px`,
      border:
        selectedRoom.class || selectedItem.class ? "0px solid red" : "2px solid red",
      backgroundColor: isRoom ? colorMap[element.type] : colorMap,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: selectedRoom
        ? element.x === selectedRoom.x
          ? "10px"
          : "0px"
        : "10px",
    };
  };

  const imageUrl = URL.createObjectURL(file);

  const handleMouseDown = (e) => {
    if (zoomLevel > 1 && !isDrawing) {
      setIsDragging(true);
      setStartDrag({ x: e.clientX - translate.x, y: e.clientY - translate.y });
    }
    else{
      setIsDragging(false);
    }
  };


  const handleMouseMove = (e) => {
    if (!isDragging || isDrawing) return;
    const x = e.clientX - startDrag.x;
    const y = e.clientY - startDrag.y;
    setTranslate({ x, y });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  return (
    <div>
      <div
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
        ref={containerRef}
        style={{
          position: "relative",
          overflow: "hidden",
          width: "677px",
          height: "577px",
        }}
      >
        <div
          style={{
            transform: `scale(${zoomLevel}) translate(${translate.x}px, ${translate.y}px)`,
            transformOrigin: "center center",
          }}
        >
          <div
            style={{
              position: "relative",
            }}
          >
            <div>
              {elements.map((element, index) => (
                <div key={index} style={getElementStyle(element)}>
                  {element.type.toUpperCase()}
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "row" }}>
            <img
              ref={floorPlanRef}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              src={imageUrl}
              style={{
                width: "677px",
                height: "577px",
              }}
            />
          </div>
        </div>
      </div>
      <div className="space add-btns">
        <button onClick={toggleSpaceCreationMode}>
          {" "}
          <img src="/assets/icons/space.svg" alt="" /> Add space
        </button>
        <button onClick={toggleWallCreationMode}>
          <img src="/assets/icons/wall.svg" alt="" /> Add wall
        </button>
      </div>
      <div className="zoom-btns">
        <img src="/assets/icons/zoom-out.svg" alt="" onClick={zoomOut} />
        <img src="/assets/icons/zoom-in.svg" alt="" onClick={zoomIn} />
      </div>
    </div>
  );
}

export default Layout;
